import { useContext, useState } from 'react';
import UserDoc from '../../../domain/models/UserDoc';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { Avatar, Button, Flex, Input, message, Pagination, Popconfirm, Skeleton, Table, Tooltip } from 'antd';
import {
  ContextStatus,
  userRoleAsString,
  userCompanyTypeAsString,
  getBestUserCompanyType,
  UserCompanyType,
} from '@leedpulse-monorepo/enums';
import { MinusCircleOutlined, SwapOutlined, UserAddOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import CompaniesContext from '../../contexts/data/companies/CompaniesContext';
import CompanyEditionFormContext, {
  COMPANY_USERS_PAGE_SIZE,
} from '../../contexts/data/companies/CompanyEditionFormContext';
import { advancedOnRow } from '../../../core/utils/commonUtils';
import { USERS_PAGE_PATH } from '../users/UsersPage';
import CompanyManageUsersModal from '../../components/companies_page/Modals/CompanyManageUsersModal';
import { ICompany } from '@leedpulse-monorepo/interfaces';

// TODO : Migrer vers algolia la liste (pour le moment fonctionne très bien mais pas viable si un jour on a plusieurs centaines de users sur une seule company)
const CompanyUsersList: React.FC = () => {
  const windowSize = useWindowSize();
  const navigate = useNavigate();
  const companiesContext = useContext(CompaniesContext);
  const companyEditionFormContext = useContext(CompanyEditionFormContext);

  const companyDoc = companiesContext.selected as ICompany;

  const [isManageUsersModalOpen, setIsManageUsersModalOpen] = useState(false);

  return (
    <>
      <Flex vertical gap={12}>
        <Flex align="center" justify="flex-start" gap={10}>
          <Input
            placeholder="Rechercher un utilisateur"
            style={{ maxWidth: '18.75rem' }}
            value={companyEditionFormContext.userSearchDraft}
            onChange={(e) => companyEditionFormContext.setUserSearchDraft(e.target.value)}
            allowClear
          />
          <Tooltip title="Gérer les utilisateurs">
            <Button type="default" icon={<SwapOutlined />} onClick={() => setIsManageUsersModalOpen(true)} />
          </Tooltip>
          <Tooltip title="Créer un nouvel utilisateur">
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() =>
                window.open(
                  `${USERS_PAGE_PATH}/new?type=user&companyId=${companyDoc.id}`,
                  '_blank'
                )
              }
            />
          </Tooltip>
          Utilisateurs ({companiesContext.companyUsers.length || '-'})
        </Flex>

        <Flex align="center" justify="space-between">
          <Flex>
            {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
            <>
              {/* Insert filters here */}
            </>
          </Flex>
          <Pagination
            current={companyEditionFormContext.usersTablePage}
            pageSize={COMPANY_USERS_PAGE_SIZE}
            showSizeChanger={false}
            total={companyEditionFormContext.usersTotalCount}
            style={{ marginLeft: 'auto' }}
            onChange={(page) =>
              void companyEditionFormContext.goToUsersPage(page)
            }
          />
        </Flex>

        {companyEditionFormContext.usersListStatus === ContextStatus.LOADING ? (
          <Skeleton active />
        ) : (
          <Table
            pagination={{
              current: companyEditionFormContext.usersTablePage,
              pageSize: COMPANY_USERS_PAGE_SIZE,
              total: companyEditionFormContext.usersTotalCount,
              showSizeChanger: false,
              position: ['bottomRight'],
              onChange: (page) => companyEditionFormContext.goToUsersPage(page),
            }}
            columns={
              windowSize.width > 576
                ? [
                  {
                    title: 'Avatar',
                    dataIndex: 'avatar',
                    key: 'avatar',
                    render: (avatar: string) => <Avatar src={avatar} size={'small'} />,
                  },
                  {
                    title: 'Utilisateur',
                    render: (record: UserDoc) => `${record?.firstname} ${record?.lastname}`,
                  },
                  {
                    title: 'Email',
                    dataIndex: 'email',
                    key: 'email',
                  },
                  {
                    title: 'Dernière connexion',
                    dataIndex: 'last_login',
                    key: 'last_login',
                    render: (_: number, oUser: UserDoc) => {
                      let color = 'gray';

                      if (oUser.last_login !== oUser.created) {
                        color = oUser.last_login === 0 || moment(oUser.last_login).isBefore(moment().subtract(1, 'month'))
                          ? 'red'
                          : oUser.last_login === 0 || moment(oUser.last_login).isBefore(moment().subtract(1, 'week'))
                            ? 'orange'
                            : 'green';
                      }

                      return <span style={{ color }}>{oUser.last_login === oUser.created ? `Jamais connecté(e)` : `Dernière connexion ${moment(oUser.last_login).fromNow()}`}</span>;
                    },
                  },
                  {
                    title: 'Droits',
                    key: 'type',
                    render: (record: UserDoc) => userCompanyTypeAsString(getBestUserCompanyType(record.idCompanies?.[companiesContext.selected?.id as string]?.roles as UserCompanyType[] || [])),
                  },
                  {
                    title: 'Rôle',
                    dataIndex: 'role',
                    key: 'role',
                    render: (role: string) => userRoleAsString(role),
                  },
                  {
                    title: '',
                    dataIndex: 'emailAuthentication',
                    key: 'emailAuthentication',
                    render: (emailAuthentication: string) => (
                      <Popconfirm
                        title="Êtes-vous sûr de vouloir retirer cet utilisateur de la compagnie ?"
                        onCancel={(e) => e?.stopPropagation()}
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          companiesContext.removeUser(emailAuthentication);
                        }}
                        okText="Oui"
                        cancelText="Non"
                      >
                        <Button
                          icon={<MinusCircleOutlined style={{ color: '#FF0000' }} />}
                          type="text"
                          onClick={(e) => e.stopPropagation()}
                          loading={
                            companiesContext.statusAddRemoveUser ===
                            ContextStatus.LOADING
                          }
                        />
                      </Popconfirm>
                    ),
                  },
                ]
                : [
                  {
                    title: 'Email',
                    dataIndex: 'email',
                    key: 'email',
                  },
                ]
            }
            rowKey={(record) => record.uid}
            dataSource={companyEditionFormContext.users}
            onRow={(record) =>
              advancedOnRow({
                url: `${USERS_PAGE_PATH}/${record.uid}`,
                navigate,
                className: 'table-row',
              })
            }
          />
        )}
      </Flex>

      <CompanyManageUsersModal
        open={isManageUsersModalOpen}
        onCancel={() => setIsManageUsersModalOpen(false)}
        onSuccess={async () => {
          message.success('Utilisateurs de l\'entreprise mis à jour');
          setIsManageUsersModalOpen(false);
          if (companyDoc?.id) {
            await companiesContext.fetchUsers(companyDoc.id);
          }
        }}
        okText="Appliquer"
        cancelText="Annuler"
        width={1100}
      />
    </>
  );
};

export default CompanyUsersList;
